import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import {
  PATH_CREATE_FROM_TEMPLATE,
  SHOW_ALL,
  SHOW_USER,
  STATE_ALL,
  TAB_ANALYTICS,
  TAB_STORIES,
  TAB_TEMPLATES,
  VIEW_LIST
} from '@opengov/stories-shared/constants';
import { IStory } from '@opengov/stories-shared/types';
import { isEqual } from 'lodash';
import React from 'react';
import { createPortal } from 'react-dom';

import {
  ISessionUser,
  ISettings,
  IStoriesFolder,
  IStoryAnalytics,
  IStoryAnalyticsPayload,
  IStoryListing,
  IStoryListMessages,
  IStoryListSelection,
  IStoryShowOptions,
  IStoryVisibilityOptions,
  ITabOptions
} from '../../../../types';

import { Analytics } from '../Analytics';
import { CreateStoryFromTemplateModal } from '../CreateStoryFromTemplateModal/CreateStoryFromTemplateModal';

import { FolderSvg } from './FolderSvg';
import styles from './StoryListContent.scss';
import { StoryListTable } from './StoryListTable';
import { StoryCardGrid } from './StoryCardGrid';

export interface IStoryListContentProps {
  showGlobalNavBar: boolean;
  analytics: IStoryAnalytics;
  canOrganizeStories: boolean;
  currentFolderId: string;
  entityId: number;
  entityName: string;
  entitySubdomain: string;
  folderStructure: IStoriesFolder[];
  hasPendingCopyFolderProcess: boolean;
  isAdmin: boolean;
  isAnalyticsLoading: boolean;
  isLoading: boolean;
  isOpenGovAdmin: boolean;
  isTemplate: boolean;
  isViewingFolder: boolean;
  lastPath: string;
  messages: IStoryListMessages;
  search: string;
  settings: ISettings;
  show: IStoryShowOptions;
  storyVisibility: IStoryVisibilityOptions;
  stories: IStoryListing;
  storiesUrl: string;
  tab: ITabOptions;
  templates: IStoryListing;
  user: ISessionUser;
  view: string;
  publicationsList: { id: string; title: string }[];
  paginationOptions?: {
    totalRows: number;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    currentPage: number;
    setPage: (page: number) => void;
    itemName: string;
  };
  clearSearchFilters(): void;
  getAnalyticsData(props: IStoryAnalyticsPayload): any;
  getAnalyticsProperty(props: { authHeader: string; google_analytics_id: string }): any;
  navigateToAllStoriesList(): void;
  navigateToSettings(): any;
  navigateToStoryView(entitySubdomain: string, storyId: string, isTemplate: boolean): any;
  setAnalyticsAuthHeader(props: { authHeader: string }): any;
  setSelectedList(newList: IStoryListSelection, entitySubdomain: string): void;
  displayCreateStoryModal(): void;
  toggleAnalyticsLoading(loading: boolean): any;
}

interface IStoryListContentState {
  selectedTemplate: IStory;
  isCreateFromTemplateModalVisible: boolean;
}

export class StoryListContent extends React.Component<IStoryListContentProps, IStoryListContentState> {
  constructor(props) {
    super(props);

    this.state = { selectedTemplate: null, isCreateFromTemplateModalVisible: false };
  }
  shouldComponentUpdate(nextProps, nextState) {
    // Only re-render if any of the following props have changedloadingStatus
    return (
      !isEqual(this.props.stories, nextProps.stories) ||
      !isEqual(this.props.templates, nextProps.templates) ||
      !isEqual(this.props.show, nextProps.show) ||
      !isEqual(this.props.tab, nextProps.tab) ||
      !isEqual(this.props.view, nextProps.view) ||
      !isEqual(this.props.isLoading, nextProps.isLoading) ||
      !isEqual(this.props.isAnalyticsLoading, nextProps.isAnalyticsLoading) ||
      !isEqual(this.props.analytics, nextProps.analytics) ||
      !isEqual(this.props.hasPendingCopyFolderProcess, nextProps.hasPendingCopyFolderProcess) ||
      !isEqual(this.state.isCreateFromTemplateModalVisible, nextState.isCreateFromTemplateModalVisible)
    );
  }

  filterContent = () => {
    const { show, user, stories, templates } = this.props;

    // Filter stories based on show selection
    let filteredStories: IStory[];
    let filteredTemplates: IStory[];
    switch (show) {
      // Show all data by default
      default:
      case SHOW_ALL:
        filteredStories = stories.data;
        filteredTemplates = templates.data;
        break;
      // Filter data based on current user id
      case SHOW_USER:
        filteredStories = stories.data.filter((story) => story.author.og_id === user.id);
        filteredTemplates = templates.data.filter((template) => template.author.og_id === user.id);
        break;
    }
    // Return final data as object for deconstructing
    return {
      stories: filteredStories,
      templates: filteredTemplates
    };
  };

  handleClick = (story: IStory, isTemplate: boolean) => {
    const { entitySubdomain, lastPath, navigateToStoryView } = this.props;

    if (lastPath === PATH_CREATE_FROM_TEMPLATE) {
      this.setState({ selectedTemplate: story, isCreateFromTemplateModalVisible: true });
    } else {
      navigateToStoryView(entitySubdomain, story._id, isTemplate);
    }
  };

  renderAnalytics = () => {
    const {
      analytics,
      getAnalyticsProperty,
      getAnalyticsData,
      isAnalyticsLoading,
      messages,
      navigateToSettings,
      setAnalyticsAuthHeader,
      settings,
      toggleAnalyticsLoading
    } = this.props;
    const { stories } = this.filterContent();

    return (
      <Analytics
        analytics={analytics}
        settings={settings}
        messages={messages}
        stories={stories}
        isAnalyticsLoading={isAnalyticsLoading}
        handleStoryClick={this.handleClick}
        setAnalyticsAuthHeader={setAnalyticsAuthHeader}
        getAnalyticsProperty={getAnalyticsProperty}
        getAnalyticsData={getAnalyticsData}
        navigateToSettings={navigateToSettings}
        toggleAnalyticsLoading={toggleAnalyticsLoading}
      />
    );
  };

  renderTemplateSelection = () => {};

  renderStories = () => {
    const {
      showGlobalNavBar,
      canOrganizeStories,
      entityId,
      entitySubdomain,
      folderStructure,
      hasPendingCopyFolderProcess,
      isAdmin,
      isLoading,
      isOpenGovAdmin,
      isViewingFolder,
      lastPath,
      messages,
      search,
      show,
      storiesUrl,
      storyVisibility,
      tab,
      view,
      publicationsList,
      clearSearchFilters,
      displayCreateStoryModal,
      navigateToAllStoriesList,
      setSelectedList
    } = this.props;
    const { stories, templates } = this.filterContent();
    let listContent;
    let gridContent;
    let activeTab = tab;

    // Handle 'Create as Template' view
    if (lastPath === PATH_CREATE_FROM_TEMPLATE) {
      activeTab = TAB_TEMPLATES;
    }

    switch (activeTab) {
      default:
      case TAB_STORIES:
        listContent = (
          <StoryListTable
            showGlobalNavBar={showGlobalNavBar}
            stories={stories}
            messages={messages}
            isTemplate={false}
            isSelectable={isAdmin && canOrganizeStories}
            onClick={this.handleClick}
            folderStructure={folderStructure}
            isViewingFolder={isViewingFolder}
            entitySubdomain={entitySubdomain}
            setSelectedList={setSelectedList}
            canOrganizeStories={canOrganizeStories}
            publicationsList={publicationsList}
            paginationOptions={this.props.paginationOptions}
          />
        );
        gridContent = (
          <StoryCardGrid
            showGlobalNavBar={showGlobalNavBar}
            stories={stories}
            storiesUrl={storiesUrl}
            isTemplate={false}
            messages={messages}
            onClick={this.handleClick}
            paginationOptions={this.props.paginationOptions}
          />
        );
        break;
      case TAB_TEMPLATES:
        const templatesByEntity = templates.filter((template: IStory) => {
          // show all templates for opengov admin
          if (isOpenGovAdmin) return true;

          // entity specific templates
          return template.entity_id === entityId;
        });

        listContent = (
          <StoryListTable
            showGlobalNavBar={showGlobalNavBar}
            messages={messages}
            stories={templatesByEntity}
            isTemplate={true}
            isSelectable={false}
            onClick={this.handleClick}
            entitySubdomain={entitySubdomain}
            isViewingFolder={isViewingFolder}
            canOrganizeStories={canOrganizeStories}
            publicationsList={publicationsList}
            paginationOptions={this.props.paginationOptions}
          />
        );
        gridContent = (
          <StoryCardGrid
            showGlobalNavBar={showGlobalNavBar}
            stories={templatesByEntity}
            storiesUrl={storiesUrl}
            isTemplate={true}
            messages={messages}
            onClick={this.handleClick}
            paginationOptions={this.props.paginationOptions}
          />
        );
        break;
    }
    // Handle various state messages
    if (isLoading || hasPendingCopyFolderProcess) {
      // Loading state
      return (
        <div className={styles.centered} data-test="story-list-loading-spinner">
          <CircularProgress />
        </div>
      );
    } else if (activeTab === TAB_STORIES && stories.length <= 0) {
      if (search.length > 0 || show === SHOW_USER || storyVisibility !== STATE_ALL) {
        // When search returns no stories
        return (
          <div className={styles.centered}>
            <h1 data-test="stories-none-found-heading">{messages.noStoriesFound}</h1>
            <h2>{isViewingFolder ? messages.adjustFiltersInFolder : messages.adjustFilters}</h2>
            <div className={styles.buttons}>
              <Button size="large" variant="outlined" color="secondary" onClick={clearSearchFilters} data-test="clear-filters-button">
                {messages.clearFilters}
              </Button>
              {isViewingFolder && (
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    navigateToAllStoriesList();
                    clearSearchFilters();
                  }}
                  data-test="view-all-stories-button"
                >
                  {messages.viewAllStories}
                </Button>
              )}
            </div>
          </div>
        );
      }

      if (isViewingFolder) {
        return (
          <div className={styles.centered}>
            <FolderSvg />
            <h1 data-test="dashboard-welcome-heading-folder">{messages.welcomeToFolder}</h1>
            <h2>{messages.addStoriesOrFolders}</h2>
          </div>
        );
      }

      // Default welcome message
      return (
        <div className={styles.centered}>
          <h1 data-test="dashboard-welcome-heading-all-stories">{messages.welcomeToStories}</h1>
          <h2>{messages.tellFirstStory}</h2>
          {isAdmin && (
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={displayCreateStoryModal}
              data-test="dashboard-new-story-button"
            >
              {messages.newStory}
            </Button>
          )}
        </div>
      );
    } else if (activeTab === TAB_TEMPLATES && templates.length <= 0) {
      if (search.length > 0) {
        // When search returns no templates
        return (
          <div className={styles.centered}>
            <h1 data-test="stories-no-templates-found-heading">{messages.noTemplatesFound}</h1>
          </div>
        );
      }
      // When user has no templates
      return (
        <div className={styles.centered}>
          <h1 data-test="stories-no-templates-yours-heading">{messages.youHaveNoTemplates}</h1>
        </div>
      );
    } else if (view === VIEW_LIST) {
      return listContent;
    }

    return gridContent;
  };

  renderModals = () => {
    const {
      canOrganizeStories,
      currentFolderId,
      folderStructure,
      entityId,
      entitySubdomain,
      isAdmin,
      user,
      settings,
      messages,
      entityName
    } = this.props;

    const { selectedTemplate, isCreateFromTemplateModalVisible } = this.state;

    return isCreateFromTemplateModalVisible
      ? createPortal(
          <CreateStoryFromTemplateModal
            isAdmin={isAdmin}
            canOrganizeStories={canOrganizeStories}
            currentFolderId={currentFolderId}
            folderStructure={folderStructure}
            messages={messages}
            entityName={entityName}
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            settings={settings}
            user={user}
            onClose={() => this.setState({ isCreateFromTemplateModalVisible: false })}
            selectedTemplate={selectedTemplate}
          />,
          document.getElementById('app')
        )
      : null;
  };

  render() {
    const { tab } = this.props;
    return (
      <main role="main" id="mainContent">
        {this.renderModals()}
        {tab === TAB_ANALYTICS ? this.renderAnalytics() : this.renderStories()}
      </main>
    );
  }
}
